import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';
import usePermissions from '../hooks/usePermissions';
import { useQuery } from '@tanstack/react-query';
import { api } from '../hooks/useAuth';

const Layout = ({ children }) => {
  const { t, i18n } = useTranslation();
  const { user, logout } = useAuth();
  const location = useLocation();
  const { canView } = usePermissions();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  console.log('🚀 [MANUAL] Layout component loaded');
  console.log('🔍 [MANUAL] User:', user);

  // Early return if user is not loaded yet
  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Fetch settings for system name
  const { data: settings } = useQuery({
    queryKey: ['settings'],
    queryFn: async () => {
      const response = await api.get('/settings');
      return response.data;
    },
    enabled: !!user, // Only fetch when user is logged in
  });

  const systemName = settings?.systemName || 'Invoice System';

  // Apply system language if available
  useEffect(() => {
    if (settings?.defaultLocale) {
      // Only apply if user hasn't manually changed language
      const savedLocale = localStorage.getItem('locale');
      if (!savedLocale) {
        i18n.changeLanguage(settings.defaultLocale);
      }
    }
  }, [settings, i18n]);

  const navigation = [
    { name: t('common.dashboard'), href: '/dashboard', icon: '📊' },
    { name: t('common.companies'), href: '/companies', icon: '🏢', module: 'companies' },
    { name: t('common.customers'), href: '/customers', icon: '👥', module: 'customers' },
    { name: t('common.employees'), href: '/employees', icon: '👷', module: 'employees' },
    { name: t('common.invoices'), href: '/invoices', icon: '📄', module: 'invoices' },
    { name: t('common.incomingInvoices'), href: '/incoming-invoices', icon: '📥', module: 'incomingInvoices' },
    { name: t('common.taxCalculation'), href: '/tax-calculation', icon: '🧮', module: 'taxCalculation' },
    { name: t('common.standardDocuments'), href: '/standard-documents', icon: '📋', module: 'standardDocuments' },
    { name: t('common.smtpSettings'), href: '/smtp-settings', icon: '📧', module: 'smtpSettings' },
    { name: t('common.mailTemplates'), href: '/mail-templates', icon: '📝', module: 'mailTemplates' },
    { name: t('common.apiDocumentation'), href: '/api-documentation', icon: '🔌', module: 'apiDocumentation' },
  ];

  // Permission-based navigation
  const filteredNavigation = navigation
    .filter(item => {
      return !item.module || canView(item.module);
    })
    .concat(
      canView('permissionGroups') ? [{ name: t('common.permissionGroups'), href: '/permission-groups', icon: '🔐', module: 'permissionGroups' }] : []
    )
    .concat(
      canView('users') ? [{ name: t('common.users'), href: '/users', icon: '👤', module: 'users' }] : []
    )
    .concat(
      canView('settings') ? [{ name: t('common.settings'), href: '/settings', icon: '⚙️', module: 'settings' }] : []
    );

  const languages = [
    { code: 'en', name: 'English', flag: '🇺🇸' },
    { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
    { code: 'tr', name: 'Türkçe', flag: '🇹🇷' },
    { code: 'ru', name: 'Русский', flag: '🇷🇺' },
  ];

  const handleLanguageChange = (langCode) => {
    i18n.changeLanguage(langCode);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile sidebar */}
      <div className={`fixed inset-0 z-50 lg:hidden ${sidebarOpen ? 'block' : 'hidden'}`}>
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={() => setSidebarOpen(false)} />
        <div className="fixed inset-y-0 left-0 flex w-64 flex-col bg-white">
          <div className="flex h-16 items-center justify-between px-4">
            <h1 className="text-xl font-bold text-gray-900">{systemName}</h1>
            <button
              onClick={() => setSidebarOpen(false)}
              className="text-gray-500 hover:text-gray-700"
            >
              ✕
            </button>
          </div>
          <nav className="flex-1 px-4 py-4 space-y-2">
            {filteredNavigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`flex items-center px-3 py-2 rounded-lg text-sm font-medium ${
                  location.pathname === item.href
                    ? 'bg-primary-100 text-primary-700'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
                onClick={() => setSidebarOpen(false)}
              >
                <span className="mr-3">{item.icon}</span>
                {item.name}
              </Link>
            ))}
          </nav>
        </div>
      </div>

      {/* Desktop sidebar */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col">
        <div className="flex flex-col flex-grow bg-white border-r border-gray-200">
          <div className="flex h-16 items-center px-4">
            <h1 className="text-xl font-bold text-gray-900">{systemName}</h1>
          </div>
          <nav className="flex-1 px-4 py-4 space-y-2">
            {filteredNavigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`flex items-center px-3 py-2 rounded-lg text-sm font-medium ${
                  location.pathname === item.href
                    ? 'bg-primary-100 text-primary-700'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                <span className="mr-3">{item.icon}</span>
                {item.name}
              </Link>
            ))}
          </nav>
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Top bar */}
        <div className="sticky top-0 z-40 bg-white border-b border-gray-200">
          <div className="flex h-16 items-center justify-between px-4 sm:px-6 lg:px-8">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden text-gray-500 hover:text-gray-700"
            >
              ☰
            </button>
            
            <div className="flex items-center space-x-4">
              {/* Language selector */}
              <div className="relative">
                <select
                  value={i18n.language}
                  onChange={(e) => handleLanguageChange(e.target.value)}
                  className="appearance-none bg-white border border-gray-300 rounded-lg px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  {languages.map((lang) => (
                    <option key={lang.code} value={lang.code}>
                      {lang.flag} {lang.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* User menu */}
              <div className="flex items-center space-x-3">
                <div className="text-sm">
                  <p className="font-medium text-gray-900">{user?.name}</p>
                  <p className="text-gray-500">{user?.email}</p>
                </div>
                <button
                  onClick={logout}
                  className="btn btn-secondary btn-sm"
                >
                  {t('common.logout')}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Page content */}
        <main className="p-4 sm:p-6 lg:p-8">
          {children}
        </main>
      </div>
    </div>
  );
};

export default Layout;
